import React, { useState, useEffect } from "react";
import { CalendarPlus } from "lucide-react";
import TimePicker from "./TimePicker";
import DatePicker from "./DatePicker";
import { defaultWhen } from "../utils/calendar";
import { getValidUrl } from "../utils/Utils";
import ImageField from "./ImageField";
const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3005";

const EventCard = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState(() => ({
        name: "",
        location: "",
        ...defaultWhen(),
        category: "",
        description: "",
        eventImage: "",
    }));
    
    const categorias = ["Charla", "Exposicion", "Networking", "Conferencia", "Ruta", "Carrera/Competición", "Feria/Salón", "Concentración", "Otras"];

    const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const today = React.useMemo(() => new Date(), []);
    const maxDate = React.useMemo(() => {
        const limit = new Date();
        limit.setFullYear(limit.getFullYear() + 1);
        return limit;
    }, []);


    useEffect(() => {
        if (isOpen) {
            // Resetear formulario cuando se abre
            // Fecha y hora vienen rellenas con hoy y la próxima media hora
            setFormData({
                name: "",
                location: "",
                ...defaultWhen(),
                category: "",
                description: "",
                eventImage: "",
            });
        }
    }, [isOpen]);

    const handleChange = (field) => (e) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const saveEventData = async (eventData) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                alert("No estás autenticado. Por favor, inicia sesión.");
                window.location.href = "/";
                return null;
            }

            const response = await fetch(`${baseUrl}/events/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(eventData),
            });

            const data = await response.json();
            if (!response.ok) {
                alert(data.message || "Error al guardar el evento");
                return null;
            }
            return data;
        } catch (error) {
            console.error("Error al guardar el evento:", error);
            alert("Error al guardar el evento");
            return null;
        }
    };

    const saveData = async () => {
        const userId = localStorage.getItem("userId");
        if (!userId) {
            alert("No se encontró el usuario.");
            return;
        }

        const eventData = {
            name: formData.name,
            location: formData.location,
            date: formData.date,
            time: formData.time,
            category: formData.category,
            description: formData.description,
            // En base64 lo sube el backend; una URL escrita a mano se normaliza
            eventImage: formData.eventImage.startsWith("data:")
                ? formData.eventImage
                : getValidUrl(formData.eventImage.trim()) || '',
            creator: userId,
        };

        const savedEvent = await saveEventData(eventData);
        if (!savedEvent) return;

        alert("Evento creado con éxito. Será revisado por los administradores.");
        window.location.reload();
    };

    const handleSubmit = () => {
        if (
            !formData.name ||
            !formData.location ||
            !formData.date ||
            !formData.time ||
            !formData.category ||
            !formData.description
        ) {
            alert("Por favor, completa todos los campos obligatorios.");
            return;
        }

        const selectedDate = new Date(formData.date);
        if (selectedDate > maxDate) {
            alert("La fecha del evento no puede superar 1 año desde hoy.");
            return;
        }

        saveData();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="dyd-overlay"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="dyd-modal"
            >
                <div className="dyd-modal-head">
          <span className="dyd-modal-icon"><CalendarPlus size={20} /></span>
          <h2>Crear nuevo evento</h2>
        </div>

                <div className="dyd-field">
                    <label className="dyd-label">
                        Nombre del evento *
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={handleChange("name")}
                        placeholder="Nombre del evento"
                        className="dyd-input"
                    />
                </div>

                <div className="dyd-field">
                    <label className="dyd-label">
                        Ubicación *
                    </label>
                    <input
                        type="text"
                        value={formData.location}
                        onChange={handleChange("location")}
                        placeholder="Dirección o lugar del evento"
                        className="dyd-input"
                    />
                </div>

                <div style={{ display: "flex", gap: "12px" }}>
                    <div className="dyd-field" style={{ flex: 1 }}>
                        <label className="dyd-label">
                            Fecha *
                        </label>
                        <DatePicker
                            value={formData.date}
                            onChange={(date) => setFormData((f) => ({ ...f, date }))}
                            min={formatDateForInput(today)}
                            max={formatDateForInput(maxDate)}
                        />
                    </div>

                    <div className="dyd-field" style={{ flex: 1 }}>
                        <label className="dyd-label">
                            Hora *
                        </label>
                        <TimePicker
                            value={formData.time}
                            onChange={(time) => setFormData((f) => ({ ...f, time }))}
                        />
                    </div>
                </div>

                <div className="dyd-field">
                    <label className="dyd-label">
                        Categoría *
                    </label>
                    <select
                        value={formData.category}
                        onChange={handleChange("category")}
                        className="dyd-select"
                    >
                        <option value="">Selecciona una categoría</option>
                        {categorias.map((cat) => (
                            <option key={cat} value={cat}>
                                {cat}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="dyd-field">
                    <label className="dyd-label">
                        Descripción *
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={handleChange("description")}
                        placeholder="Describe el evento, actividades, requisitos, etc."
                        rows={4}
                        className="dyd-textarea"
                    />
                </div>

                <ImageField
                    label="Imagen del evento (opcional)"
                    value={formData.eventImage}
                    onChange={(eventImage) => setFormData((f) => ({ ...f, eventImage }))}
                    hint="Si no pones ninguna, se usará una portada con las iniciales del evento."
                />

                <div className="dyd-note">
                    <small className="dyd-hint">
                        <strong>Nota:</strong> Tu evento se publicará al crearse.
                    </small>
                </div>

                <div className="dyd-modal-actions">
                    <button
                        onClick={handleSubmit}
                        className="dyd-btn dyd-btn-success"
                    >
                        Crear evento
                    </button>

                    <button
                        onClick={onClose}
                        className="dyd-btn dyd-btn-subtle"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EventCard;
